import { Badge, Button, Group, Modal, SegmentedControl, Stack, Text, UnstyledButton } from "@mantine/core"
import { useMemo, useState } from "react"
import { adaptiveFractionDigits, formatFixedNumber, formatNumber, formatPercent } from "atlas_forecasting_app/lib/format"
import { clamp01 } from "atlas_forecasting_app/lib/math"
import {
  approximateQuantileFromNumericForecast,
  buildDiscreteBuckets,
  extractNumericDistribution,
  multipleChoiceSemantics,
  statsFromDiscrete,
} from "atlas_forecasting_app/forecasting/forecast_utils"
import { BinaryGaugeChart } from "atlas_forecasting_app/components/charts/binary_gauge_chart"
import { DiscreteHistogramChart } from "atlas_forecasting_app/components/charts/discrete_histogram_chart"
import { MultipleChoiceBarChart } from "atlas_forecasting_app/components/charts/multiple_choice_bar_chart"
import { NumericCdfChart } from "atlas_forecasting_app/components/charts/numeric_cdf_chart"

function SummaryBadges({ items, mb = 0 }) {
  const visibleItems = items.filter((item) => item?.value)
  if (visibleItems.length === 0) return null

  return (
    <Group gap="xs" mb={mb}>
      {visibleItems.map((item) => (
        <Badge key={item.label} variant="light" radius="xl" size="lg">
          <strong>{item.label}</strong> {item.value}
        </Badge>
      ))}
    </Group>
  )
}

function DistributionViewToggle({ mode, onChange }) {
  return (
    <SegmentedControl
      size="xs"
      value={mode}
      onChange={onChange}
      data={[
        { label: "PMF", value: "pmf" },
        { label: "CMF", value: "cmf" },
      ]}
      aria-label="Distribution view"
    />
  )
}

function MultipleChoiceForecastDisplay({ question, forecastData, compact = false, previewLimit }) {
  const [open, setOpen] = useState(false)
  const semantics = multipleChoiceSemantics(question)

  if (!forecastData || typeof forecastData !== "object") return <Text c="dimmed">Invalid forecast data.</Text>

  const entries = Object.entries(forecastData)
    .map(([k, v]) => [k, clamp01(Number(v))])
    .filter(([, v]) => v != null)
    .sort((a, b) => b[1] - a[1])

  const [topLabel, topP] = entries[0] || []

  const limit = Number(previewLimit)
  const hasPreviewLimit = Number.isFinite(limit) && limit > 0 && entries.length > limit

  let displayEntries = entries
  if (compact && entries.length > 9) {
    if (semantics.canAggregateRemaining) {
      const top = entries.slice(0, 8)
      const rest = entries.slice(8)
      const restSum = rest.reduce((acc, [, p]) => acc + (p || 0), 0)
      const otherLabel = entries.some(([label]) => String(label).toLowerCase().includes("other")) ? "Remaining listed" : "Other listed"
      displayEntries = restSum > 0 ? top.concat([[otherLabel, restSum]]) : top
    } else {
      displayEntries = entries.slice(0, 8)
    }
  } else if (hasPreviewLimit) {
    displayEntries = entries.slice(0, limit)
  }

  const optionsLabel = hasPreviewLimit ? `${entries.length} (showing ${limit})` : `${entries.length}`

  const chart = <MultipleChoiceBarChart entries={displayEntries} height={compact ? 220 : undefined} valueLabel={semantics.valueLabel} />

  return (
    <Stack gap="md">
      <SummaryBadges
        mb={0}
        items={[
          topLabel ? { label: semantics.topLabel, value: `${topLabel} (${formatPercent(topP, 1)})` } : null,
          { label: "Options", value: optionsLabel },
        ]}
      />
      {semantics.note ? (
        <Text size="sm" c="dimmed">
          {semantics.note}
        </Text>
      ) : null}

      {hasPreviewLimit ? (
        <UnstyledButton
          type="button"
          className="af-chart-button"
          onClick={() => setOpen(true)}
          aria-label={`View all ${entries.length} options`}
        >
          {chart}
        </UnstyledButton>
      ) : (
        chart
      )}

      <Modal opened={open} onClose={() => setOpen(false)} title={`All options (${entries.length})`} size="xl">
        <Stack gap="md">
          <SummaryBadges
            mb={0}
            items={[
              topLabel ? { label: semantics.topLabel, value: `${topLabel} (${formatPercent(topP, 1)})` } : null,
              { label: "Options", value: String(entries.length) },
            ]}
          />
          {semantics.note ? (
            <Text size="sm" c="dimmed">
              {semantics.note}
            </Text>
          ) : null}
          <MultipleChoiceBarChart entries={entries} valueLabel={semantics.valueLabel} />
        </Stack>
      </Modal>
    </Stack>
  )
}

function DiscreteForecastDisplay({ question, forecastData, compact = false }) {
  const [mode, setMode] = useState("pmf")

  const buckets = buildDiscreteBuckets(question, forecastData)
  if (!buckets) return <Text c="dimmed">Invalid forecast data.</Text>

  const stats = statsFromDiscrete(buckets)

  return (
    <Stack gap="md">
      {stats ? (
        <Group justify="space-between" align="center" gap="sm">
          <SummaryBadges
            items={[
              { label: "Mean", value: stats.mean == null ? "—" : formatNumber(stats.mean, 2) },
              { label: "Median", value: stats.p50Label || "—" },
              { label: "80%", value: stats.p10Label && stats.p90Label ? `${stats.p10Label}–${stats.p90Label}` : "—" },
              { label: "Mode", value: `${stats.modeLabel || "—"} (${formatPercent(stats.maxP, 1)})` },
            ]}
          />
          <DistributionViewToggle mode={mode} onChange={setMode} />
        </Group>
      ) : null}
      <DiscreteHistogramChart buckets={buckets} mode={mode} height={compact ? 200 : 260} />
    </Stack>
  )
}

function NumericForecastDisplay({ question, forecastData, compact = false }) {
  const [mode, setMode] = useState("pmf")

  const distribution = extractNumericDistribution(forecastData)
  if (!distribution) return <Text c="dimmed">Invalid forecast data.</Text>

  const p10 = approximateQuantileFromNumericForecast(forecastData, 0.1, question)
  const p50 = approximateQuantileFromNumericForecast(forecastData, 0.5, question)
  const p90 = approximateQuantileFromNumericForecast(forecastData, 0.9, question)
  const summaryFractionDigits = useMemo(
    () => adaptiveFractionDigits([question?.lower_bound, question?.upper_bound, p10, p50, p90]),
    [question?.lower_bound, question?.upper_bound, p10, p50, p90]
  )

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center" gap="sm">
        <Group gap="xs">
          <Badge variant="transparent" c="dimmed" px={0}>
            Percentiles
          </Badge>
          <SummaryBadges
            items={[
              { label: "10%", value: p10 == null ? "—" : formatFixedNumber(p10, summaryFractionDigits) },
              { label: "Median", value: p50 == null ? "—" : formatFixedNumber(p50, summaryFractionDigits) },
              { label: "90%", value: p90 == null ? "—" : formatFixedNumber(p90, summaryFractionDigits) },
            ]}
          />
        </Group>
        <DistributionViewToggle mode={mode} onChange={setMode} />
      </Group>
      <NumericCdfChart distribution={distribution} question={question} mode={mode} height={compact ? 200 : 260} />
    </Stack>
  )
}

export function ForecastDisplay({ question, forecastData, compact = false, multipleChoicePreviewLimit }) {
  if (!question) return null
  const qType = question.question_type

  if (forecastData == null) return <Text c="dimmed">No forecast yet.</Text>

  if (qType === "binary") {
    const p = clamp01(Number(forecastData))
    if (p == null) return <Text c="dimmed">Invalid forecast data.</Text>

    return (
      <Stack gap="md">
        <SummaryBadges
          items={[
            { label: "P(Yes)", value: formatPercent(p, 2) },
            { label: "P(No)", value: formatPercent(1 - p, 2) },
          ]}
        />
        <BinaryGaugeChart p={p} height={compact ? 170 : 220} />
      </Stack>
    )
  }

  if (qType === "multiple_choice") {
    return <MultipleChoiceForecastDisplay question={question} forecastData={forecastData} compact={compact} previewLimit={multipleChoicePreviewLimit} />
  }

  if (qType === "discrete") {
    return <DiscreteForecastDisplay question={question} forecastData={forecastData} compact={compact} />
  }

  if (qType === "numeric") {
    return <NumericForecastDisplay question={question} forecastData={forecastData} compact={compact} />
  }

  return <Text c="dimmed">Unknown question type.</Text>
}
